import { cn } from "@/lib/cn"
import type {
  CustomFieldDefinition,
  CustomFieldStatus,
  CustomFieldType,
} from "@/types/custom_field"

// Single row for the custom-fields index. Click anywhere on the row to
// open the slide-over drawer — the parent owns selection state. All
// mutations (edit / archive / promote) live in the drawer; rows are
// click-to-open and otherwise read-only.
//
// Grid columns (mirrored on the section header):
//   1.7fr name+description | 1fr key | 1fr type | 0.7fr status

// Desktop: 4-col grid (name | key | type | status). Mobile: rows stack —
// name+badges on top, then key/type/status as an inline strip below.
export const CUSTOM_FIELD_GRID_COLS =
  "md:grid md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.7fr)] md:items-center md:gap-2"

type Props = {
  definition: CustomFieldDefinition
  selected?: boolean
  onClick?: () => void
}

const STATUS_LABEL: Record<CustomFieldStatus, string> = {
  pending:  "Pending",
  live:     "Active",
  archived: "Archived",
}

// Per-status pill styling. "Active" = success green, "Pending" = amber,
// "Archived" = neutral gray.
const STATUS_PILL: Record<CustomFieldStatus, string> = {
  pending:  "border-status-warning/30 bg-status-warning/10 text-status-warning",
  live:     "border-status-success/30 bg-status-success/10 text-status-success",
  archived: "border-border bg-white/5 text-app-muted",
}

// Type icon presentation — colored prefix box + glyph + label. Mirrors
// the design's color-coded type indicators so operators can scan field
// types at a glance.
type TypeStyle = { glyph: string; bg: string; fg: string; label: string }
const TYPE_STYLE: Record<CustomFieldType, TypeStyle> = {
  text:         { glyph: "Aa",  bg: "bg-sky-500/15",        fg: "text-sky-300",        label: "Text" },
  number:       { glyph: "123", bg: "bg-blue-500/15",       fg: "text-blue-300",       label: "Number" },
  boolean:      { glyph: "✓",   bg: "bg-status-success/15", fg: "text-status-success", label: "Checkbox" },
  date:         { glyph: "▦",   bg: "bg-status-warning/15", fg: "text-status-warning", label: "Date" },
  select:       { glyph: "▾",   bg: "bg-violet-500/15",     fg: "text-violet-300",     label: "Dropdown" },
  multi_select: { glyph: "▾▾",  bg: "bg-pink-500/15",       fg: "text-pink-300",       label: "Multi-select" },
}

export function TypeBadge({ type, size = "sm" }: { type: CustomFieldType; size?: "sm" | "md" }) {
  const style = TYPE_STYLE[type]
  const sizing = size === "md" ? "px-2.5 py-1.5 text-sm" : "px-2 py-1 text-xs"
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-control", style.bg, sizing)}>
      <span className={cn("font-mono text-[10px] font-semibold", style.fg)}>{style.glyph}</span>
      <span className={cn("font-medium", style.fg)}>{style.label}</span>
    </span>
  )
}

export function CustomFieldRow({
  definition,
  selected,
  onClick,
}: Props) {
  return (
    <div
      className={cn(
        CUSTOM_FIELD_GRID_COLS,
        "border-b border-border/60 px-4 py-3 last:border-b-0 transition cursor-pointer",
        selected ? "bg-brand-red/5" : "hover:bg-white/[0.02]",
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.() }
      }}
    >
      {/* Name + description */}
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 truncate text-sm font-medium text-app-text">
          <span className="truncate">{definition.label}</span>
          {definition.pii && (
            <span className="inline-flex items-center rounded-chip border border-brand-red/30 bg-brand-redSoft px-1.5 py-0.5 text-[9px] font-medium uppercase text-brand-red" title="Contains PII">
              PII
            </span>
          )}
          {definition.reserved ? (
            <span className="inline-flex items-center rounded-chip border border-border bg-app-panel/40 px-1.5 py-0.5 text-[9px] font-medium uppercase text-app-subtle" title="System-reserved field. You can curate the choice list here; the per-user value is set by your partner integration.">
              SYS
            </span>
          ) : definition.llmVisible && (
            <span className="inline-flex items-center rounded-chip border border-vip-gold/30 bg-vip-goldMute px-1.5 py-0.5 text-[9px] font-medium uppercase text-vip-goldSoft" title="Visible to AI">
              AI
            </span>
          )}
        </div>
        {definition.description && (
          <div className="truncate text-[11px] text-app-muted">{definition.description}</div>
        )}
      </div>

      {/* Mobile: key/type/status wrap into a single flex strip below the
          name. Desktop: each gets its own grid cell. */}
      <div className="mt-2 flex flex-wrap items-center gap-2 md:contents md:mt-0">
        {/* Key — mono pill */}
        <div className="min-w-0">
          <code className="block max-w-full truncate rounded-control border border-border bg-app-panel/40 px-2 py-1 font-mono text-[11px] text-app-muted">
            {definition.key}
          </code>
        </div>

        {/* Type */}
        <div className="min-w-0">
          <TypeBadge type={definition.fieldType} />
        </div>

        {/* Status */}
        <div>
          <span className={cn(
            "inline-flex items-center gap-1.5 rounded-chip border px-2 py-0.5 text-[11px] font-medium",
            STATUS_PILL[definition.status],
          )}>
            <span className={cn(
              "inline-block h-1.5 w-1.5 rounded-full",
              definition.status === "live" && "bg-status-success",
              definition.status === "pending" && "bg-status-warning",
              definition.status === "archived" && "bg-app-subtle",
            )} />
            {STATUS_LABEL[definition.status]}
          </span>
        </div>
      </div>
    </div>
  )
}
